import React, { useMemo } from 'react';
import { Card, Button } from 'antd';
import styles from './s.module.less';
import { EClosedFunnelFilterType, IClosedFunnelFilter } from 'types/operation';
import { TreatmentOptions, TitleText, GrowthTierOptions, LabelText } from '../MatchingProviderEditModal';

interface MatchingProviderProps {
    filters: IClosedFunnelFilter[]; // 改为接收filters数组
    onEdit?: () => void;
}

const MatchingProvider: React.FC<MatchingProviderProps> = ({ filters, onEdit }) => {
    const { title, treatmentText, excludedMedicationsText, otherFilterContent } = useMemo(() => {
        if (!filters || filters.length === 0 || filters.every((filter) => !filter.filterValues)) {
            return {
                title: TitleText[filters[0].filterType],
                treatmentText: '',
                excludedMedicationsText: '',
                otherFilterContent: '',
            };
        }

        // 检查是否是治疗类型的filters（需要合并处理）
        const treatmentTypeFilters = filters.filter((filter) =>
            filter.filterType === EClosedFunnelFilterType.TREATMENT || filter.filterType === EClosedFunnelFilterType.NON_PRESCRIBABLE_MEDICATION,
        );

        // 如果是治疗类型的filters，进行合并处理
        if (treatmentTypeFilters.length > 0 && treatmentTypeFilters.length === filters.length) {
            const allTreatments: string[] = [];
            const allExcludedMedications: string[] = [];

            // 遍历所有filters，合并数据
            filters.forEach((filter) => {
                try {
                    // 尝试解析JSON格式的filterValues
                    const filterData = JSON.parse(filter.filterValues);

                    // 合并treatments
                    if (filterData.treatments && Array.isArray(filterData.treatments)) {
                        allTreatments.push(...filterData.treatments);
                    }

                    // 合并excludedMedications
                    if (filterData.excludedMedications && Array.isArray(filterData.excludedMedications)) {
                        allExcludedMedications.push(...filterData.excludedMedications);
                    }
                } catch (error) {
                    // 如果解析失败，按逗号分隔字符串处理（向后兼容）
                    if (filter.filterType === EClosedFunnelFilterType.TREATMENT) {
                        const treatments = filter.filterValues.split(',').filter((t) => t.trim());
                        allTreatments.push(...treatments);
                    } else if (filter.filterType === EClosedFunnelFilterType.NON_PRESCRIBABLE_MEDICATION) {
                        // 处理NON_PRESCRIBABLE_MEDICATION类型的逗号分隔字符串
                        const medications = filter.filterValues.split(',').filter((m) => m.trim());
                        allExcludedMedications.push(...medications);
                    }
                }
            });

            // 去重
            const uniqueTreatments = Array.from(new Set(allTreatments));
            const uniqueExcludedMedications = Array.from(new Set(allExcludedMedications));

            // 将treatment值转换为对应的label
            const treatmentLabels = uniqueTreatments.map((item: string) =>
                TreatmentOptions.find((option) => option.value === item)?.label,
            ).filter(Boolean);

            return {
                title: 'Filter by treatment',
                treatmentText: treatmentLabels.join(', '),
                excludedMedicationsText: uniqueExcludedMedications.join(', '),
                otherFilterContent: '',
            };
        } else {
            // 对于其他类型的filter，显示其基本信息
            const filter = filters[0]; // 单个filter
            const filterTypeName = filter.filterType.replace(/_/g, ' ').toLowerCase()
                .replace(/\b\w/g, (l) => l.toUpperCase());

            if (filter.filterType === EClosedFunnelFilterType.GROWTH_TIER) {
                const growthTierLabels = filter.filterValues.split(',').map((item: string) =>
                    GrowthTierOptions.find((option) => option.value === item)?.label,
                ).filter(Boolean);

                return {
                    title: TitleText[filter.filterType],
                    otherFilterContent: growthTierLabels.join(', '),
                };
            }

            return {
                title: TitleText[filter.filterType] || `Filter by ${filterTypeName}`,
                treatmentText: '',
                excludedMedicationsText: '',
                otherFilterContent: filter.filterValues,
            };
        }
    }, [filters]);

    // 判断是否是治疗类型的filters
    const isTreatmentType = filters.every((filter) =>
        filter.filterType === EClosedFunnelFilterType.TREATMENT || filter.filterType === EClosedFunnelFilterType.NON_PRESCRIBABLE_MEDICATION,
    );

    return (
        <Card className={styles.infoCard} bordered={false}>
            <div className={styles.header}>
                <span className={styles.title}>{title}</span>
                <Button onClick={onEdit}>Edit</Button>
            </div>

            {isTreatmentType ? (
                // 治疗类型的filter显示方式
                <>
                    <div className={styles.item}>
                        <div className={styles.label}>Treatment</div>
                        <div className={styles.value}>{treatmentText || 'Not provided'}</div>
                    </div>
                    <div className={styles.item}>
                        <div className={styles.label}>Exclude providers who don’t prescribe</div>
                        <div className={styles.value}>{excludedMedicationsText || 'Not provided'}</div>
                    </div>
                </>
            ) : (
                // 其他类型的filter显示方式
                <div className={styles.item}>
                    <div className={styles.label}>{LabelText[filters[0].filterType as keyof typeof LabelText] || 'Filter Values'} </div>
                    <div className={styles.value}>{otherFilterContent || 'Not provided'}</div>
                </div>
            )}
        </Card>
    );
};

export default MatchingProvider;
